import { ErrorModal, getMomentTz } from "@dexteel/mesf-core";
import { FormControl, FormHelperText, Grid2, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { PartFields } from "@/pages/configuration/pages/parts/PartFields";
import { CommentsField } from "@/shared/components/CommentsField";
import { Typography } from "@mui/material";
import { CalendarIcon, DatePicker } from "@mui/x-date-pickers";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { upsertCassetteChock } from "../../../repositories/CassetteChockRepository";

import { FooterDetailsButtons } from "@/controls/buttons/FooterDetailsButtons";
import {
	CASSETTE_CHOCK_INITIAL_VALUES,
	CassetteChock,
} from "@/pages/catalogs/models/CassetteChock";
import { HelmetDexteel } from "@dexteel/mesf-core";

const moment = getMomentTz();

type Props = {
	cassetteChock: CassetteChock | null;
	modal: "create" | "update" | "delete" | "";
	onHide: ({
		shouldUpdate,
		close,
		entityId,
	}: {
		shouldUpdate: boolean;
		close: boolean;
		entityId?: number;
	}) => void;
};

export const CassetteChockDetails = ({
	cassetteChock,
	modal,
	onHide,
}: Props) => {
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);

	const {
		control,
		reset,
		handleSubmit,
		getValues,
		formState: { dirtyFields },
	} = useForm<CassetteChock>({
		defaultValues: CASSETTE_CHOCK_INITIAL_VALUES,
		mode: "onChange",
	});

	const isFormDirty =
		!(Object.keys(dirtyFields).length === 0) || modal == "create";

	const queryClient = useQueryClient();
	const upsertMutation = useMutation(upsertCassetteChock, {
		meta: { successMessage: `The cassette chock was ${modal}d successfully` },
		onSuccess: async (id: number) => {
			await queryClient.invalidateQueries({ queryKey: ["cassetteChocks"] });
			onHide({ shouldUpdate: true, close: false, entityId: id });
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const onSubmit: SubmitHandler<CassetteChock> = async (data) => {
		setIsSubmitLoading(true);
		if (modal !== "delete") {
			if (cassetteChock?.IsDuplicate) {
				upsertMutation.mutate({ ...data, PartId: null });
			} else {
				upsertMutation.mutate(data);
			}
		}
	};

	const validateNotInFuture = (description: string, value: Date | null) => {
		if (value && value > new Date()) {
			return `${description} time cannot be in the future.`;
		}
		return true;
	};

	useEffect(() => {
		if (modal !== "") {
			reset(cassetteChock || CASSETTE_CHOCK_INITIAL_VALUES);
			setIsSubmitLoading(false);
		}
	}, [modal, cassetteChock]);

	return (
		<>
			{(modal === "create" || modal === "update") && (
				<HelmetDexteel
					title={`${
						cassetteChock === null
							? "New Cassette Chock"
							: `${cassetteChock?.PartName ?? ""}`
					} - Cassette Chocks`}
				/>
			)}
			{cassetteChock === null && modal !== "create" ? (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
						width: "100%",
					}}
				>
					<Typography>Select a cassette chock or create a new one</Typography>
				</div>
			) : (
				<form onSubmit={handleSubmit(onSubmit)}>
					<Grid2 container spacing={1} p={1}>
						<PartFields<CassetteChock>
							partTypeCode="K"
							control={control}
							disabled={modal === "delete"}
						/>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="RebuildTimestamp"
								control={control}
								shouldUnregister
								rules={{
									validate: {
										notInFuture: (value) =>
											validateNotInFuture("Rebuild", value),
									},
								}}
								render={({ field, fieldState: { error } }) => (
									<FormControl fullWidth error={!!error}>
										<DatePicker
											disabled={modal === "delete"}
											label="Rebuild Date"
											format="MM/DD/YYYY"
											views={["year", "month", "day"]}
											value={field.value ? moment(field.value) : null}
											onChange={field.onChange}
											slots={{
												openPickerButton: (props) => (
													<IconButton
														id="rebuild-date-calendar-button"
														size="small"
														onClick={props.onClick}
													>
														<CalendarIcon />
													</IconButton>
												),
											}}
											slotProps={{
												textField: {
													size: "small",
													error: !!error,
												},
											}}
										/>
										{error && <FormHelperText>{error.message}</FormHelperText>}
									</FormControl>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="BearingChangeTimestamp"
								control={control}
								shouldUnregister
								rules={{
									validate: {
										notInFuture: (value) =>
											validateNotInFuture("Bearing Change", value),
									},
								}}
								render={({ field, fieldState: { error } }) => (
									<FormControl fullWidth error={!!error}>
										<DatePicker
											disabled={modal === "delete"}
											label="Bearing Change Date"
											format="MM/DD/YYYY"
											views={["year", "month", "day"]}
											value={field.value ? moment(field.value) : null}
											onChange={field.onChange}
											slots={{
												openPickerButton: (props) => (
													<IconButton
														id="bearing-change-date-calendar-button"
														size="small"
														onClick={props.onClick}
													>
														<CalendarIcon />
													</IconButton>
												),
											}}
											slotProps={{
												textField: {
													size: "small",
													error: !!error,
												},
											}}
										/>
										{error && <FormHelperText>{error.message}</FormHelperText>}
									</FormControl>
								)}
							/>
						</Grid2>
						<Grid2 size={{ md: 12, xs: 12 }}>
							<Controller
								name="Comments"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<CommentsField
										value={field.value}
										onChange={field.onChange}
										fieldError={error}
										getValues={getValues}
										disabled={modal === "delete"}
									/>
								)}
							/>
						</Grid2>
						<FooterDetailsButtons
							isSubmitLoading={isSubmitLoading}
							disabled={!isFormDirty}
							onHide={() => onHide({ shouldUpdate: false, close: true })}
						/>
					</Grid2>
				</form>
			)}
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
